import React, { useCallback } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { ObjectDetailsSpacer } from '@atlassian/jira-servicedesk-insight-attribute-views/src/common/ui/styled.tsx';
import { UsersReadView } from '@atlassian/jira-servicedesk-insight-attribute-views/src/ui/user/index.tsx';
import {
	useUserValue,
	getUserValues,
} from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/user/index.tsx';
import { useEditableAttribute } from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/use-editable-attribute/index.tsx';
import type { UserAttributeType } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute-type.tsx';
import type { UserInlineEditValue } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/picker-options.tsx';
import { UserAttributeUserPicker } from '@atlassian/jira-servicedesk-insight-user-attribute-picker/src/ui/index.tsx';
import { InlineEditableAttribute } from '../../common/ui/editable-attribute/index.tsx';

type Props = {
	isEditable: boolean;
	attributeType: UserAttributeType;
};

const toFieldValue = (users: Array<UserInlineEditValue>) => users.map(({ accountId }) => accountId);

const isUserInlineEditValue = (editValue: unknown): editValue is UserInlineEditValue =>
	typeof editValue === 'object' &&
	editValue != null &&
	'displayName' in editValue &&
	'accountId' in editValue &&
	'avatarUrl' in editValue;

const isUserInlineEditValueArray = (editValues: unknown): editValues is UserInlineEditValue[] =>
	editValues != null &&
	Array.isArray(editValues) &&
	editValues.length > 0 &&
	isUserInlineEditValue(editValues[0]);

const EditableUserAttribute = (props: Props) => {
	const { attributeType, isEditable } = props;

	const [{ attribute }] = useEditableAttribute(attributeType);
	const [value, setValue] = useUserValue(attribute);

	const resetValue = useCallback(() => setValue(getUserValues(attribute)), [attribute, setValue]);
	const storedValue = toFieldValue(getUserValues(attribute));
	const onValueChange = useCallback(
		(newUsers: unknown) => {
			if (
				isUserInlineEditValueArray(newUsers) ||
				(Array.isArray(newUsers) && newUsers.length === 0)
			) {
				setValue(newUsers);
			}
		},
		[setValue],
	);
	const isMultiPicker = attributeType.maximumCardinality !== 1;

	return (
		<InlineEditableAttribute
			testId="servicedesk-insight-inline-editable-attributes.ui.user-attribute"
			value={toFieldValue(value)}
			onChange={onValueChange}
			storedValue={storedValue}
			attributeType={attributeType}
			attributeValuesInfo={{ type: 'user', attributeValues: value }}
			scalingEnabled
			isEditable={isEditable}
			renderReadView={() => (
				<ObjectDetailsSpacer>
					<UsersReadView
						users={value.map(({ accountId, avatarUrl, displayName }) => ({
							accountId,
							avatarUrl,
							displayName,
						}))}
					/>
				</ObjectDetailsSpacer>
			)}
			renderEditView={({ hasValidationError, onChange, isClearable, shouldInputRenderValue }) => (
				<UserAttributeUserPicker
					isClearable={isClearable}
					shouldInputRenderValue={shouldInputRenderValue}
					autoFocus
					value={value}
					isMultiUserPicker={isMultiPicker}
					scopedGroups={attributeType.typeValueMulti ?? []}
					onChange={onChange}
					hasValidationError={hasValidationError}
				/>
			)}
			onResetValue={resetValue}
		/>
	);
};

export const UserEditableAttribute = (props: Props) => (
	<JSErrorBoundary
		id="InsightEditableUserAttribute"
		packageName="jiraServicedeskInsightEditableUserAttribute"
		teamName="falcons"
		fallback="unmount"
	>
		<EditableUserAttribute {...props} />
	</JSErrorBoundary>
);
